import React, { useEffect, useMemo, useState } from "react";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Image as ImageIcon, Lock, ShieldCheck, BadgeCheck, Search, X, Calendar, History, Download, RefreshCw, AlertTriangle, MessageCircle } from "lucide-react";
import { customerApi, fmtErr } from "@/customer/api";
import { requestSecureDownload } from "@/customer/downloads";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("en-ZA", { dateStyle: "medium" }); } catch { return iso; } };

export default function CustomerPurchased() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [eventFilter, setEventFilter] = useState("all");
  const [open, setOpen] = useState(null);
  const [downloading, setDownloading] = useState({});

  useEffect(() => {
    let active = true;
    customerApi.get("/customer/purchased-photos")
      .then((r) => { if (active) setPhotos(r.data); })
      .catch((e) => toast.error(fmtErr(e)))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const reload = async () => {
    try {
      const r = await customerApi.get("/customer/purchased-photos");
      setPhotos(r.data);
      // Sync modal state with refreshed data
      setOpen((cur) => cur ? r.data.find((x) => x.id === cur.id) || null : cur);
    } catch (e) { /* soft */ }
  };

  const handleDownload = async (pp) => {
    if (downloading[pp.id]) return;
    setDownloading((s) => ({ ...s, [pp.id]: true }));
    try {
      const r = await requestSecureDownload(pp.id);
      toast.success(`Download started · ${r.filename}`);
      await reload();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) toast.error("Please log in again to continue.");
      else if (status === 403) toast.error("You do not have permission to download this photo.");
      else if (status === 404) toast.error(fmtErr(e) || "This photo is not available for download yet.");
      else toast.error(fmtErr(e) || "We could not prepare your download. Please try again.");
    } finally {
      setDownloading((s) => ({ ...s, [pp.id]: false }));
    }
  };

  const events = useMemo(() => {
    const m = new Map();
    photos.forEach((p) => p.event_id && !m.has(p.event_id) && m.set(p.event_id, p.event_title));
    return Array.from(m, ([id, title]) => ({ id, title }));
  }, [photos]);

  const list = useMemo(() => photos.filter((p) =>
    (eventFilter === "all" || p.event_id === eventFilter) &&
    (!q || (p.display_name || "").toLowerCase().includes(q.toLowerCase()))
  ), [photos, q, eventFilter]);

  return (
    <div data-testid="page-customer-purchased" className="max-w-[1280px] mx-auto px-5 sm:px-8 pt-32 pb-24 space-y-8">
      <SEO title="CustomerPurchased · WelkomBiz Photography" noindex />
      <header className="space-y-3">
        <Link to="/customer" data-testid="purchased-back" className="inline-flex items-center gap-2 text-[10px] tracking-luxe uppercase text-white/55 hover:text-white">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Dashboard
        </Link>
        <p className="text-[11px] tracking-luxe uppercase text-white/45">— My Purchased Photos</p>
        <h1 className="font-display text-4xl sm:text-5xl leading-[1.05]">
          <span className="text-chrome">Your </span>
          <span className="italic text-magcyan">moments</span>
          <span className="text-chrome">, locked in.</span>
        </h1>
      </header>

      <div className="glass chrome-border rounded-md p-4 sm:p-5 flex items-start gap-3" data-testid="purchased-info">
        <ShieldCheck className="w-4 h-4 text-[#ff2e93] mt-0.5 shrink-0" />
        <p className="text-xs sm:text-sm text-white/65 font-body">
          Tap any photo to preview and download the original full-quality file. Each download uses a fresh 10-minute secure link —
          you can re-download any photo you purchased <strong>without</strong> spending additional credits.
        </p>
      </div>

      {loading ? <p className="text-sm text-white/40">Loading…</p> :
       photos.length === 0 ? (
        <div data-testid="purchased-empty" className="glass chrome-border rounded-md p-12 text-center">
          <ImageIcon className="w-7 h-7 text-[#ff2e93] mx-auto mb-4" />
          <h2 className="font-display text-2xl text-chrome">No purchased photos yet</h2>
          <p className="text-sm text-white/55 mt-2">Purchased photos will appear here after you confirm a credit purchase.</p>
          <div className="mt-5 flex justify-center gap-3 flex-col sm:flex-row">
            <Link to="/events" data-testid="purchased-empty-browse" className="btn-primary !py-2.5 !px-5 !text-[11px]">Browse Events</Link>
            <Link to="/customer/purchases" data-testid="purchased-empty-history" className="btn-ghost !py-2.5 !px-5 !text-[11px]"><History className="w-3.5 h-3.5" /> Purchase History</Link>
          </div>
        </div>
       ) : (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-3 flex-1 glass chrome-border rounded-full px-4 py-2">
              <Search className="w-4 h-4 text-white/45" />
              <input data-testid="purchased-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search photos…" className="flex-1 bg-transparent text-sm text-white placeholder-white/35 outline-none font-body" />
            </div>
            <div className="flex gap-2 flex-wrap">
              <button data-testid="purchased-filter-all" onClick={() => setEventFilter("all")} className={`text-[10px] tracking-luxe uppercase px-3 py-1.5 rounded-full border ${eventFilter === "all" ? "border-transparent bg-gradient-to-r from-[#ff2e93] to-[#18d3ff] text-white" : "border-white/15 text-white/55 hover:text-white"}`}>All Events</button>
              {events.map((ev) => (
                <button key={ev.id} data-testid={`purchased-filter-${ev.id}`} onClick={() => setEventFilter(ev.id)} className={`text-[10px] tracking-luxe uppercase px-3 py-1.5 rounded-full border ${eventFilter === ev.id ? "border-transparent bg-gradient-to-r from-[#ff2e93] to-[#18d3ff] text-white" : "border-white/15 text-white/55 hover:text-white"}`}>{ev.title}</button>
              ))}
            </div>
            <Link to="/customer/purchases" data-testid="purchased-history-link" className="btn-ghost !py-2 !px-4 !text-[10px]"><History className="w-3.5 h-3.5" /> Purchase History</Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
            {list.map((p) => {
              const missing = p.download_status === "missing_original" || p.original_available === false;
              const isDownloading = !!downloading[p.id];
              return (
              <div key={p.id} data-testid={`purchased-photo-${p.id}`} className="group relative photo-frame chrome-border aspect-square overflow-hidden">
                <button onClick={() => setOpen(p)} aria-label="Preview" className="absolute inset-0 z-0">
                  {p.thumbnail_url
                    ? <img src={p.thumbnail_url} alt={p.display_name} loading="lazy" className="w-full h-full object-cover" />
                    : <div className="w-full h-full bg-black/60 flex items-center justify-center text-white/30"><ImageIcon className="w-6 h-6" /></div>}
                </button>
                <span className={`absolute top-2 left-2 z-10 inline-flex items-center gap-1 text-[9px] tracking-luxe uppercase px-2 py-0.5 rounded-full glass-strong border ${missing ? "border-[#ffd479]/40 text-[#ffd479]" : "border-[#7fe6ff]/40 text-[#7fe6ff]"}`}>
                  {missing ? <><AlertTriangle className="w-3 h-3" /> Missing</> : <><BadgeCheck className="w-3 h-3" /> {p.download_count > 0 ? "Downloaded" : "Ready"}</>}
                </span>
                <div className="absolute inset-x-0 bottom-0 z-10 p-2 flex flex-col gap-1" style={{background:"linear-gradient(180deg, transparent, rgba(0,0,0,0.92))"}}>
                  <p className="text-[10px] tracking-luxe uppercase text-white truncate">{p.display_name}</p>
                  <p className="text-[9px] text-white/55 truncate">{p.event_title}</p>
                  <button
                    data-testid={`purchased-download-${p.id}`}
                    onClick={(e) => { e.stopPropagation(); handleDownload(p); }}
                    disabled={missing || isDownloading}
                    className={`mt-1 inline-flex items-center justify-center gap-1.5 text-[10px] tracking-luxe uppercase rounded-full px-2.5 py-1.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${missing ? "border border-white/15 text-white/45" : "bg-gradient-to-r from-[#ff2e93] to-[#18d3ff] text-white"}`}
                  >
                    {missing ? <><Lock className="w-3 h-3" /> Unavailable</> : isDownloading ? <><RefreshCw className="w-3 h-3 animate-spin" /> Preparing…</> : <><Download className="w-3 h-3" /> Download{p.download_count > 0 ? " · Again" : ""}</>}
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        </>
       )
      }

      {/* Preview modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm" onClick={() => setOpen(null)}>
          <div onClick={(e) => e.stopPropagation()} data-testid="purchased-photo-modal" className="glass-strong chrome-border rounded-md p-3 sm:p-5 max-w-5xl w-full max-h-[94vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="min-w-0">
                <p className="text-[10px] tracking-luxe uppercase text-white/45">{open.event_title} · {open.subfolder_name}</p>
                <h3 className="font-display text-xl sm:text-2xl text-chrome truncate">{open.display_name}</h3>
                <p className="text-[10px] tracking-luxe uppercase text-white/45 mt-1"><Calendar className="w-3 h-3 inline mr-1" />Purchased {fmtDate(open.purchased_at)}</p>
              </div>
              <button data-testid="purchased-modal-close" onClick={() => setOpen(null)} className="w-9 h-9 rounded-full chrome-border bg-black/40 text-white flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>
            <div className="photo-frame chrome-border bg-black/60 max-h-[68vh] flex items-center justify-center">
              {open.preview_url
                ? <img src={open.preview_url} alt={open.display_name} className="w-full h-auto max-h-[68vh] object-contain" />
                : <div className="w-full h-[40vh] flex items-center justify-center text-white/30"><ImageIcon className="w-10 h-10" /></div>}
            </div>
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3 text-[11px] text-white/55 font-body">
                <ShieldCheck className="w-3.5 h-3.5 text-[#ff2e93]" />
                Secure one-time download link · expires in 10 minutes
                {open.download_count > 0 && (
                  <span className="ml-2 text-white/40">· Downloaded {open.download_count}× </span>
                )}
              </div>
              {open.download_status === "missing_original" || open.original_available === false ? (
                <div className="flex items-center gap-3">
                  <span data-testid="purchased-modal-missing" className="inline-flex items-center gap-1.5 text-[10px] tracking-luxe uppercase px-3 py-2 rounded-full glass-strong border border-[#ffd479]/40 text-[#ffd479]">
                    <AlertTriangle className="w-3 h-3" /> Original missing
                  </span>
                  <Link to="/contact" className="text-[10px] tracking-luxe uppercase text-white/70 hover:text-white inline-flex items-center gap-1">
                    <MessageCircle className="w-3 h-3" /> Contact support
                  </Link>
                </div>
              ) : (
                <button
                  data-testid="purchased-modal-download"
                  onClick={() => handleDownload(open)}
                  disabled={!!downloading[open.id]}
                  className="btn-primary !py-2.5 !px-4 !text-[10px] disabled:opacity-60"
                >
                  {downloading[open.id] ? <><RefreshCw className="w-3 h-3 animate-spin" /> Preparing…</> : <><Download className="w-3 h-3" /> {open.download_count > 0 ? "Download Again" : "Download Full-Quality Photo"}</>}
                </button>
              )}
            </div>
            {open.last_downloaded_at && (
              <p className="text-[10px] tracking-luxe uppercase text-white/40 mt-2">
                Last downloaded {fmtDate(open.last_downloaded_at)}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
